import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import BusinessIcon from "@mui/icons-material/Business";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BlockIcon from "@mui/icons-material/Block";
import GroupsIcon from "@mui/icons-material/Groups";
import EditIcon from "@mui/icons-material/Edit";
import toast from "react-hot-toast";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import ListSummaryCards, {
  countWhere,
} from "../../../Components/List/ListSummaryCards";
import ClientSelectAutocomplete from "./ClientSelectAutocomplete";
import {
  assignApplicationToClient,
  deactivateApplicationForClient,
  fetchClientApplicationContext,
  fetchClientsForSelect,
  updateClientApplicationSubscription,
} from "../../../Apis/ClientApplications";

function formatDateDisplay(value) {
  if (!value) return "—";
  const s = String(value).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }
  return s || "—";
}

function toDateInputValue(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function subscriptionFromApp(app) {
  return {
    license_count: app?.license_count ?? 0,
    plan_id: app?.plan_id ?? "",
    subscription_plan: app?.subscription_plan || "",
    subscription_status: app?.subscription_status || "active",
    start_date: toDateInputValue(app?.start_date),
    end_date: toDateInputValue(app?.end_date),
  };
}

const ASSIGNED_APP_COLUMNS = [
  {
    field: "application_name",
    label: "Application",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "status_label",
    label: "Status",
    visible: true,
    width: 110,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "license_count",
    label: "Licenses",
    visible: true,
    width: 100,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "plan_label",
    label: "Plan",
    visible: true,
    width: 160,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "subscription_status",
    label: "Subscription",
    visible: true,
    width: 130,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "start_label",
    label: "Start",
    visible: true,
    width: 120,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "end_label",
    label: "End",
    visible: true,
    width: 120,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "row_actions",
    label: "Actions",
    visible: true,
    width: 110,
    filterable: false,
    sortable: false,
    pinned: "none",
    required: false,
  },
];

const subscriptionInitial = {
  license_count: 0,
  plan_id: "",
  subscription_plan: "",
  subscription_status: "active",
  start_date: "",
  end_date: "",
};

function addDaysToDateInput(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function todayDateInput() {
  return addDaysToDateInput(0);
}

function planLabel(plan) {
  if (!plan) return "";
  const cycle = plan.billing_cycle ? ` · ${plan.billing_cycle}` : "";
  const price =
    plan.price === null || plan.price === undefined
      ? plan.contact_sales
        ? " · Contact sales"
        : ""
      : ` · ₹${plan.price}`;
  return `${plan.plan_name}${cycle}${price}`;
}

export default function AssignClientApplication() {
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [contextError, setContextError] = useState(null);
  const [applicationId, setApplicationId] = useState("");
  const [subscription, setSubscription] = useState(subscriptionInitial);
  const [showSubscription, setShowSubscription] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [appToDeactivate, setAppToDeactivate] = useState(null);
  const [subscriptionErrors, setSubscriptionErrors] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [appToEdit, setAppToEdit] = useState(null);
  const [editForm, setEditForm] = useState(subscriptionInitial);
  const [editErrors, setEditErrors] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setClientsLoading(true);
      try {
        const list = await fetchClientsForSelect();
        if (!cancelled) {
          setClients(
            (list || []).map((c) => ({
              client_id: c.client_id,
              client_name: c.client_name || `Client #${c.client_id}`,
            }))
          );
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err.response?.data?.error || err.message || "Failed to load clients."
          );
        }
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadContext = useCallback(async (clientId) => {
    if (!clientId) {
      setContext(null);
      return;
    }
    setContextLoading(true);
    setContextError(null);
    try {
      setContext(await fetchClientApplicationContext(clientId));
    } catch (err) {
      setContext(null);
      setContextError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load client application context."
      );
    } finally {
      setContextLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedClient?.client_id) {
      setContext(null);
      setApplicationId("");
      return;
    }
    loadContext(selectedClient.client_id);
    setApplicationId("");
  }, [selectedClient, loadContext]);

  const appsToAdd = useMemo(() => {
    if (!context?.catalog_applications?.length) return [];
    const assignedIds = new Set(
      (context.assigned_applications || [])
        .filter((a) => a.is_active)
        .map((a) => Number(a.application_id))
    );
    return context.catalog_applications.filter(
      (app) => !assignedIds.has(Number(app.application_id))
    );
  }, [context]);

  const openEditDialog = (app) => {
    setAppToEdit(app);
    setEditForm(subscriptionFromApp(app));
    setEditErrors({});
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    if (savingEdit) return;
    setEditDialogOpen(false);
    setAppToEdit(null);
    setEditErrors({});
  };

  const validateSubscription = (form) => {
    const errors = {};
    if (!form.start_date) errors.start_date = "Start date is required.";
    if (!form.end_date) errors.end_date = "End date is required.";
    else if (form.start_date && form.end_date < form.start_date) {
      errors.end_date = "End date must be on or after start date.";
    }
    return errors;
  };

  const saveEditSubscription = async () => {
    if (!selectedClient?.client_id || !appToEdit) return;
    const errors = validateSubscription(editForm);
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      toast.error("Please fix subscription dates.");
      return;
    }
    setEditErrors({});
    setSavingEdit(true);
    try {
      const result = await updateClientApplicationSubscription(
        selectedClient.client_id,
        appToEdit.application_id,
        {
          license_count: Number(editForm.license_count) || 0,
          plan_id: editForm.plan_id ? Number(editForm.plan_id) : null,
          subscription_plan: editForm.subscription_plan || null,
          subscription_status: editForm.subscription_status || null,
          start_date: editForm.start_date,
          end_date: editForm.end_date,
        }
      );
      toast.success(result.message || "Subscription updated.");
      await loadContext(selectedClient.client_id);
      closeEditDialog();
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors?.start_date || apiErrors?.end_date) {
        setEditErrors({
          start_date: apiErrors.start_date?.[0] || "",
          end_date: apiErrors.end_date?.[0] || "",
        });
      }
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to update subscription."
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const openDeactivateDialog = (app) => {
    if (!app?.is_active) return;
    setAppToDeactivate(app);
    setDeactivateDialogOpen(true);
  };

  const closeDeactivateDialog = () => {
    if (deactivatingId) return;
    setDeactivateDialogOpen(false);
    setAppToDeactivate(null);
  };

  const confirmDeactivate = async () => {
    if (!selectedClient?.client_id || !appToDeactivate?.is_active) return;

    setDeactivatingId(appToDeactivate.application_id);
    try {
      const result = await deactivateApplicationForClient(
        selectedClient.client_id,
        { application_id: Number(appToDeactivate.application_id) }
      );
      toast.success(result.message || "Application deactivated.");
      await loadContext(selectedClient.client_id);
      setDeactivateDialogOpen(false);
      setAppToDeactivate(null);
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to deactivate application."
      );
    } finally {
      setDeactivatingId(null);
    }
  };

  const linkedOrgCount = useMemo(() => {
    if (!appToDeactivate || !context?.organizations?.length) return 0;
    const appId = Number(appToDeactivate.application_id);
    return context.organizations.filter((org) =>
      (org.linked_application_ids || []).includes(appId)
    ).length;
  }, [appToDeactivate, context?.organizations]);

  const emsApplicationId = context?.ems_application_id
    ? Number(context.ems_application_id)
    : null;
  const emsPlans = context?.ems_plans || [];

  const selectedAppSupportsPlans = useMemo(() => {
    if (!applicationId || !context?.catalog_applications) return false;
    const app = context.catalog_applications.find(
      (a) => Number(a.application_id) === Number(applicationId)
    );
    return Boolean(app?.supports_catalog_plans) ||
      (emsApplicationId !== null && Number(applicationId) === emsApplicationId);
  }, [applicationId, context?.catalog_applications, emsApplicationId]);

  const editAppSupportsPlans = Boolean(
    appToEdit?.supports_catalog_plans ||
      (emsApplicationId !== null &&
        Number(appToEdit?.application_id) === emsApplicationId)
  );

  const applyEmsPlanToForm = (setter, planId) => {
    const plan = emsPlans.find((p) => Number(p.plan_id) === Number(planId));
    if (!plan) {
      setter((p) => ({ ...p, plan_id: planId || "" }));
      return;
    }
    const next = {
      plan_id: plan.plan_id,
      subscription_plan: plan.plan_name,
      license_count: plan.default_license_count ?? 0,
      subscription_status: plan.is_trial ? "trial" : "active",
    };
    if (plan.is_trial) {
      next.start_date = todayDateInput();
      next.end_date = addDaysToDateInput(30);
    }
    setter((p) => ({ ...p, ...next }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient?.client_id) {
      toast.error("Select a client.");
      return;
    }
    if (!applicationId) {
      toast.error("Select an application to assign to the client.");
      return;
    }

    const errors = validateSubscription(subscription);
    if (Object.keys(errors).length > 0) {
      setSubscriptionErrors(errors);
      setShowSubscription(true);
      toast.error("Please fill in subscription start and end dates.");
      return;
    }
    setSubscriptionErrors({});

    setSubmitting(true);
    try {
      const result = await assignApplicationToClient(selectedClient.client_id, {
        application_id: Number(applicationId),
        license_count: Number(subscription.license_count) || 0,
        plan_id: subscription.plan_id ? Number(subscription.plan_id) : null,
        subscription_plan: subscription.subscription_plan || null,
        subscription_status: subscription.subscription_status || null,
        start_date: subscription.start_date,
        end_date: subscription.end_date,
      });

      toast.success(result.message || "Application assigned to client.");
      await loadContext(selectedClient.client_id);
      setApplicationId("");
      setSubscription(subscriptionInitial);
      setSubscriptionErrors({});
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors?.start_date || apiErrors?.end_date) {
        setSubscriptionErrors({
          start_date: apiErrors.start_date?.[0] || "",
          end_date: apiErrors.end_date?.[0] || "",
        });
        setShowSubscription(true);
      }
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          (apiErrors
            ? Object.values(apiErrors).flat().join(" ")
            : null) ||
          err.message ||
          "Failed to assign application to client."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const canAssign =
    !!applicationId &&
    appsToAdd.length > 0 &&
    !!subscription.start_date &&
    !!subscription.end_date;

  const assignedAppRows = useMemo(
    () =>
      (context?.assigned_applications || []).map((app) => ({
        ...app,
        status_label: app.is_active ? "Active" : "Inactive",
        plan_label: app.catalog_plan_name || app.subscription_plan || "—",
        start_label: formatDateDisplay(app.start_date),
        end_label: formatDateDisplay(app.end_date),
      })),
    [context]
  );

  const summaryCards = useMemo(
    () => [
      {
        key: "assigned",
        label: "Assigned apps",
        value: assignedAppRows.length,
        iconKey: "apps",
      },
      {
        key: "active",
        label: "Active",
        value: countWhere(assignedAppRows, (row) => Boolean(row.is_active)),
        iconKey: "check",
        tone: "ok",
      },
      {
        key: "licenses",
        label: "Licenses",
        value: assignedAppRows.reduce(
          (sum, row) => sum + (Number(row.license_count) || 0),
          0
        ),
        iconKey: "shield",
      },
    ],
    [assignedAppRows]
  );

  const renderAssignedCell = useCallback(
    (row, col) => {
      if (col?.key !== "row_actions") return undefined;
      return (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Tooltip title="Edit subscription">
            <IconButton
              size="small"
              color="primary"
              onClick={() => openEditDialog(row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.is_active && (
            <Tooltip title="Deactivate application">
              <IconButton
                size="small"
                color="error"
                onClick={() => openDeactivateDialog(row)}
                disabled={deactivatingId === row.application_id}
              >
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    },
    [deactivatingId]
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Layout4
        heading="Assign Application to Client"
        showAddButton={false}
      />
      <ListSummaryCards cards={summaryCards} />

      <Paper sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <BusinessIcon color="primary" />
          <Typography variant="h6">Client subscription</Typography>
        </Box>

        {clientsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <ClientSelectAutocomplete
              clients={clients}
              selectedClient={selectedClient}
              onChange={(_, value) => setSelectedClient(value)}
            />

            {contextLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {contextError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {contextError}
              </Alert>
            )}

            {context && !contextLoading && (
              <>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Client applications &amp; subscriptions
                  </Typography>
                  {assignedAppRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      None yet.
                    </Typography>
                  ) : (
                    <TableDataGeneric
                      tableName="assigned_client_applications"
                      primaryKey="application_id"
                      heading="Assigned applications"
                      data={assignedAppRows}
                      sortname="application_name"
                      showActions={false}
                      hideToolbar={true}
                      token={localStorage.getItem("token")}
                      configss={ASSIGNED_APP_COLUMNS}
                      renderCell={renderAssignedCell}
                    />
                  )}
                </Box>

                <FormControl fullWidth margin="normal" required>
                  <InputLabel id="client-app-label">Application to add</InputLabel>
                  <Select
                    labelId="client-app-label"
                    label="Application to add"
                    value={applicationId}
                    onChange={(e) => {
                      setApplicationId(e.target.value);
                      setSubscription((p) => ({
                        ...p,
                        plan_id: "",
                        subscription_plan: "",
                      }));
                    }}
                  >
                    <MenuItem value="">
                      <em>Select application</em>
                    </MenuItem>
                    {appsToAdd.map((app) => (
                      <MenuItem key={app.application_id} value={app.application_id}>
                        {app.application_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {appsToAdd.length === 0 && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Client already has all active products from the catalog.
                  </Alert>
                )}

                <Button
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => setShowSubscription((v) => !v)}
                >
                  {showSubscription ? "Hide" : "Show"} subscription details
                </Button>

                <Collapse in={showSubscription}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    <TextField
                      label="License count"
                      type="number"
                      inputProps={{ min: 0 }}
                      value={subscription.license_count}
                      onChange={(e) =>
                        setSubscription((p) => ({
                          ...p,
                          license_count: e.target.value,
                        }))
                      }
                      fullWidth
                    />
                    {selectedAppSupportsPlans ? (
                      <FormControl fullWidth>
                        <InputLabel id="ems-plan-label">EMS plan</InputLabel>
                        <Select
                          labelId="ems-plan-label"
                          label="EMS plan"
                          value={subscription.plan_id || ""}
                          onChange={(e) =>
                            applyEmsPlanToForm(setSubscription, e.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>Select plan (default: Trial on assign)</em>
                          </MenuItem>
                          {emsPlans.map((plan) => (
                            <MenuItem key={plan.plan_id} value={plan.plan_id}>
                              {planLabel(plan)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        label="Subscription plan"
                        value={subscription.subscription_plan}
                        onChange={(e) =>
                          setSubscription((p) => ({
                            ...p,
                            subscription_plan: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                    )}
                    <TextField
                      label="Subscription status"
                      value={subscription.subscription_status}
                      onChange={(e) =>
                        setSubscription((p) => ({
                          ...p,
                          subscription_status: e.target.value,
                        }))
                      }
                      fullWidth
                    />
                    <TextField
                      label="Start date"
                      type="date"
                      required
                      error={!!subscriptionErrors.start_date}
                      helperText={subscriptionErrors.start_date}
                      InputLabelProps={{ shrink: true }}
                      value={subscription.start_date}
                      onChange={(e) => {
                        setSubscription((p) => ({ ...p, start_date: e.target.value }));
                        setSubscriptionErrors((prev) => ({ ...prev, start_date: "" }));
                      }}
                      fullWidth
                    />
                    <TextField
                      label="End date"
                      type="date"
                      required
                      error={!!subscriptionErrors.end_date}
                      helperText={subscriptionErrors.end_date}
                      InputLabelProps={{ shrink: true }}
                      value={subscription.end_date}
                      onChange={(e) => {
                        setSubscription((p) => ({ ...p, end_date: e.target.value }));
                        setSubscriptionErrors((prev) => ({ ...prev, end_date: "" }));
                      }}
                      fullWidth
                    />
                  </Box>
                </Collapse>

                <Box sx={{ mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      submitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    disabled={submitting || !canAssign}
                  >
                    Assign to client
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Paper>

      <Dialog
        open={editDialogOpen}
        onClose={closeEditDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="edit-subscription-dialog-title"
      >
        <DialogTitle id="edit-subscription-dialog-title">
          Edit subscription — {appToEdit?.application_name}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mt: 1,
            }}
          >
            <TextField
              label="License count"
              type="number"
              inputProps={{ min: 0 }}
              value={editForm.license_count}
              onChange={(e) =>
                setEditForm((p) => ({ ...p, license_count: e.target.value }))
              }
              fullWidth
            />
            {editAppSupportsPlans ? (
              <FormControl fullWidth>
                <InputLabel id="edit-ems-plan-label">EMS plan</InputLabel>
                <Select
                  labelId="edit-ems-plan-label"
                  label="EMS plan"
                  value={editForm.plan_id || ""}
                  onChange={(e) =>
                    applyEmsPlanToForm(setEditForm, e.target.value)
                  }
                >
                  <MenuItem value="">
                    <em>Select plan</em>
                  </MenuItem>
                  {emsPlans.map((plan) => (
                    <MenuItem key={plan.plan_id} value={plan.plan_id}>
                      {planLabel(plan)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Subscription plan"
                value={editForm.subscription_plan}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, subscription_plan: e.target.value }))
                }
                fullWidth
              />
            )}
            <TextField
              label="Subscription status"
              value={editForm.subscription_status}
              onChange={(e) =>
                setEditForm((p) => ({
                  ...p,
                  subscription_status: e.target.value,
                }))
              }
              fullWidth
            />
            <Box />
            <TextField
              label="Start date"
              type="date"
              required
              error={!!editErrors.start_date}
              helperText={editErrors.start_date}
              InputLabelProps={{ shrink: true }}
              value={editForm.start_date}
              onChange={(e) => {
                setEditForm((p) => ({ ...p, start_date: e.target.value }));
                setEditErrors((prev) => ({ ...prev, start_date: "" }));
              }}
              fullWidth
            />
            <TextField
              label="End date"
              type="date"
              required
              error={!!editErrors.end_date}
              helperText={editErrors.end_date}
              InputLabelProps={{ shrink: true }}
              value={editForm.end_date}
              onChange={(e) => {
                setEditForm((p) => ({ ...p, end_date: e.target.value }));
                setEditErrors((prev) => ({ ...prev, end_date: "" }));
              }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEditDialog} disabled={savingEdit}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveEditSubscription}
            disabled={savingEdit}
            startIcon={
              savingEdit ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
            }
          >
            {savingEdit ? "Saving…" : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deactivateDialogOpen}
        onClose={closeDeactivateDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="deactivate-app-dialog-title"
      >
        <DialogTitle id="deactivate-app-dialog-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Deactivate application?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to deactivate{" "}
            <strong>{appToDeactivate?.application_name}</strong> for client{" "}
            <strong>{context?.client_name || selectedClient?.client_name}</strong>.
          </DialogContentText>

          <Alert severity="warning" sx={{ mb: 2 }}>
            This action blocks all users of this client from logging into and using this
            product until it is assigned again.
          </Alert>

          {appToDeactivate && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Subscription details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Plan: {appToDeactivate.subscription_plan || "—"} · Status:{" "}
                {appToDeactivate.subscription_status || "—"} · Licenses:{" "}
                {appToDeactivate.license_count ?? 0}
              </Typography>
              {(appToDeactivate.start_date || appToDeactivate.end_date) && (
                <Typography variant="body2" color="text.secondary">
                  Period: {formatDateDisplay(appToDeactivate.start_date)} to{" "}
                  {formatDateDisplay(appToDeactivate.end_date)}
                </Typography>
              )}
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle2" gutterBottom>
            What will happen
          </Typography>
          <List dense disablePadding>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <BlockIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Client subscription will be marked inactive"
                secondary="is_active set to off for this client–application link"
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <GroupsIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText
                primary={
                  linkedOrgCount > 0
                    ? `${linkedOrgCount} organization(s) will lose access to this app`
                    : "Organization access for this app will be turned off"
                }
                secondary="Users will see an access denied message when they try to sign in"
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeactivateDialog} disabled={!!deactivatingId}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeactivate}
            disabled={!!deactivatingId}
            startIcon={
              deactivatingId ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {deactivatingId ? "Deactivating…" : "Deactivate application"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
